import { API_BASE_URL } from "../../../config";
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

// Same classification convention as PaymentCodeReferenceSeeder (saledata-module):
// CASH-<value> = cash denomination, BT = bank transfer (locked), FUEL = fuel expense.
const CASH_PREFIX = 'CASH-';
const BANK_TRANSFER_CODE = 'BT';
const FUEL_CODE = 'FUEL';

// แถวในตาราง "แจกแจงเงินสด" มาจากตาราง payment_code_references หมวด CASH_DENOMINATION
// ไม่ได้ฝังไว้ในหน้านี้อีกแล้ว (Vector 2026-09-21 "ให้ระบบดึงเอา ไม่ hard code") — ชุดเดียวกับที่
// เครื่อง IMD ใช้สร้างช่องกรอก ⇒ หน้านี้กับหน้าที่คนขับกรอก จะแตกชุดกันไม่ได้อีก
// รายการสำรองนี้ใช้เฉพาะตอนเรียก API ไม่สำเร็จ เพื่อไม่ให้ตารางหาย
const FALLBACK_DENOMS = [
    { value: 1000, name: '1,000 บาท' },
    { value: 500, name: '500 บาท' },
    { value: 100, name: '100 บาท' },
    { value: 50, name: '50 บาท' },
    { value: 20, name: '20 บาท' },
    { value: 1, name: 'เงินเหรียญ' },
];

// Same "จำนวนการใช้ N × 5" promo formula used in tablet-report-app/RoundDetailScreen.tsx (DDS parity) —
// keep in sync if that formula ever changes.
const extractBagMultiplier = (code) => {
    const m = code?.match(/(\d+)B/i);
    return m ? Math.max(1, Math.floor(Number(m[1]) / 100)) : 1;
};

const fmt = (n, decimals = 2) => {
    const num = typeof n === 'string' ? parseFloat(n) : (typeof n === 'number' ? n : 0);
    if (isNaN(num)) return (0).toFixed(decimals);
    return num.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

const fmtInt = (n) => {
    const num = typeof n === 'string' ? parseInt(n) : (typeof n === 'number' ? n : 0);
    return isNaN(num) ? '0' : num.toLocaleString();
};

const todayStr = () => {
    const d = new Date();
    const pad = (x) => String(x).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Derive the paper-slip-shaped summary for one driver report from the existing /full payload —
// every source field here (itemsBreakdown/refills/cargos/bagReturns/payments/promotions) already
// exists from earlier rounds of this workstream; this page only aggregates + relabels, no new backend.
const buildSummary = (full, denoms) => {
    if (!full) return null;
    const items = full.itemsBreakdown?.items || [];
    const refills = full.meltCompensations || full.refills || [];
    const cargos = full.cargos || [];
    const bagReturns = full.bagReturns || [];
    const payments = full.payments || [];
    const promoUsages = full.promotions?.usages || [];

    const productMap = new Map();
    const ensureProduct = (name) => {
        if (!productMap.has(name)) productMap.set(name, { name, sellQty: 0, sellTotal: 0, refillQty: 0, cargoQty: 0 });
        return productMap.get(name);
    };
    items.forEach(it => {
        const row = ensureProduct(it.productName || it.iceCode || '-');
        row.sellQty += it.quantity || 0;
        row.sellTotal += it.total || 0;
    });
    refills.forEach(r => {
        const row = ensureProduct(r.productName || r.code || '-');
        row.refillQty += r.quantity || 0;
    });
    cargos.forEach(c => {
        const row = ensureProduct(c.productName || c.code || '-');
        row.cargoQty += c.quantity || 0;
    });

    // BagReturnData.quantity is signed: positive = กระสอบกลับมา, negative = กระสอบขาดเกิน.
    // productName is already resolved server-side (icecys-kb dds gotcha fix) — no green/red label at all.
    const bagMap = new Map();
    bagReturns.forEach(b => {
        const name = b.productName || b.code || '-';
        if (!bagMap.has(name)) bagMap.set(name, { name, back: 0, deficit: 0 });
        const row = bagMap.get(name);
        const q = b.quantity || 0;
        if (q >= 0) row.back += q; else row.deficit += -q;
    });

    const promoUsageCount = promoUsages.reduce(
        (sum, u) => sum + (u.times || 0) * extractBagMultiplier(u.promoCode || ''),
        0
    );

    let fuelTotal = 0;
    let transferTotal = 0;
    // ชนิดเงินที่ "ยังนับอยู่ตอนนี้" = แถวที่ระบบส่งมา · ใบเก่าอาจอ้างรหัสที่เลิกนับแยกแล้ว
    // (CASH-2/5/10 ก่อน 2026-09-21) ⇒ พับยอดพวกนั้นลงแถวค่าเงินต่ำสุดที่ยังมีอยู่ ซึ่งก็คือช่อง
    // เหรียญ (ค่าเงิน 1 ⇒ จำนวน = บาท พอดี) ไม่ใช้เกณฑ์ "<= 10" ฝังไว้ เพราะถ้าวันหลังระบบ
    // เลิกนับ 20 แยก เกณฑ์ที่ฝังไว้จะกลายเป็นผิดเงียบ ๆ
    const denomList = (denoms && denoms.length ? denoms : FALLBACK_DENOMS)
        .slice()
        .sort((a, b) => b.value - a.value);
    const knownValues = new Set(denomList.map(d => d.value));
    const foldValue = denomList.length ? denomList[denomList.length - 1].value : 1;
    const cashDenoms = {}; // value -> {count, total}
    denomList.forEach(d => { cashDenoms[d.value] = { count: 0, total: 0 }; });
    payments.forEach(p => {
        const code = p.paymentCode || '';
        if (code === FUEL_CODE) {
            fuelTotal += p.total || 0;
        } else if (code === BANK_TRANSFER_CODE) {
            transferTotal += p.total || 0;
        } else if (code.startsWith(CASH_PREFIX)) {
            const value = Number(code.slice(CASH_PREFIX.length)) || 0;
            const count = p.amount || 0;
            const total = p.total || 0;
            const target = knownValues.has(value) ? value : foldValue;
            if (!cashDenoms[target]) cashDenoms[target] = { count: 0, total: 0 };
            // แถวที่ถูกพับมา นับ "ใบ" รวมกันไม่ได้ (เหรียญ 5 หนึ่งเหรียญ != เหรียญ 1 หนึ่งเหรียญ)
            // จึงนับเป็นยอดบาทแทน ซึ่งตรงกับความหมายของช่องเหรียญอยู่แล้ว
            cashDenoms[target].count += (target === value ? count : total);
            cashDenoms[target].total += total;
        }
    });
    const cashGrandTotal = Object.values(cashDenoms).reduce((s, x) => s + x.total, 0);

    return {
        products: Array.from(productMap.values()),
        bagReturns: Array.from(bagMap.values()),
        saleTotal: full.itemsBreakdown?.totalPrice || 0,
        promoUsageCount,
        fuelTotal,
        transferTotal,
        denomList,
        cashDenoms,
        cashGrandTotal,
    };
};

const sectionStyle = { background: '#fff', borderRadius: '8px', boxShadow: '0 1px 4px rgba(0,0,0,0.10)', marginBottom: '16px', overflow: 'hidden', border: '1px solid #E5E5E5' };
const cardHeaderStyle = { background: '#E5E5E5', color: '#000', padding: '10px 16px', fontWeight: 700, fontSize: '14px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' };
const headerStyle = { background: '#f8f9fa', fontWeight: 700, borderBottom: '2px solid #E5E5E5', padding: '8px 12px', fontSize: '13px', color: '#495057' };
const rowStyle = { borderBottom: '1px solid #E5E5E5', padding: '6px 12px' };

const DriverDailyReport = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [date, setDate] = useState(searchParams.get('date') || todayStr());
    const [reports, setReports] = useState([]);
    const [fullByCode, setFullByCode] = useState({});
    const [denoms, setDenoms] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState({});

    const checkToken = useCallback(() => {
        const user = localStorage.getItem('user');
        if (!user) { navigate('/login'); return null; }
        return JSON.parse(user)?.token;
    }, [navigate]);

    const fetchAll = useCallback(async (d) => {
        const token = checkToken();
        if (!token) return;
        setLoading(true);
        try {
            const listRes = await fetch(`${API_BASE_URL}/saledata/api/module/reports/by-date?startDate=${d}&endDate=${d}&isAgent=false`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const listRaw = listRes.ok ? await listRes.json() : [];
            const list = Array.isArray(listRaw) ? listRaw : [];
            setReports(list);

            const fulls = await Promise.all(
                list.map(r =>
                    fetch(`${API_BASE_URL}/saledata/api/module/reports/${encodeURIComponent(r.reportCode)}/full`, {
                        headers: { Authorization: `Bearer ${token}` }
                    }).then(res => res.ok ? res.json() : null).catch(() => null)
                )
            );
            const map = {};
            list.forEach((r, i) => { map[r.reportCode] = fulls[i]; });
            setFullByCode(map);

            // ชนิดเงินที่ยังนับอยู่ — ดึงจากตารางอ้างอิงชุดเดียวกับที่เครื่อง IMD ใช้
            const refsRes = await fetch(`${API_BASE_URL}/saledata/api/module/payment-codes?category=CASH_DENOMINATION`, {
                headers: { Authorization: `Bearer ${token}` }
            }).catch(() => null);
            const refsRaw = refsRes && refsRes.ok ? await refsRes.json() : [];
            setDenoms(
                (Array.isArray(refsRaw) ? refsRaw : [])
                    .filter(r => r.paymentValue != null)
                    .map(r => ({ value: Number(r.paymentValue), name: r.name || `${r.paymentValue}` }))
            );
        } finally {
            setLoading(false);
        }
    }, [checkToken]);

    useEffect(() => {
        fetchAll(date);
        setSearchParams({ date });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date]);

    const toggleExpand = (code) => setExpanded(prev => ({ ...prev, [code]: !prev[code] }));

    return (
        <div className="btc-container" style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
                <h2 style={{ margin: 0 }}>รายงานรายคนขับ (Driver Daily Report)</h2>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button className="btc-btn" style={{ background: '#6B6B6B', padding: '4px 10px', fontSize: '13px' }} onClick={() => navigate(-1)}>← กลับ</button>
                    <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ padding: '4px 8px', border: '1px solid #E5E5E5', borderRadius: '4px' }} />
                </div>
            </div>

            {loading && <p style={{ color: '#6B6B6B' }}>กำลังโหลด...</p>}
            {!loading && reports.length === 0 && <p style={{ color: '#6B6B6B' }}>ไม่มีรายงานวันนี้</p>}

            {!loading && reports.map(r => {
                const full = fullByCode[r.reportCode];
                const summary = buildSummary(full, denoms);
                const isOpen = !!expanded[r.reportCode];
                return (
                    <div key={r.reportCode} style={sectionStyle}>
                        <div style={cardHeaderStyle} onClick={() => toggleExpand(r.reportCode)}>
                            <span>{r.nameuser || r.sellerId} — {r.reportCode}</span>
                            <span style={{ fontWeight: 400, fontSize: '13px', color: '#495057' }}>
                                ยอดขาย {fmt(summary?.saleTotal, 0)} ฿ · เงินสดนับได้ {fmt(summary?.cashGrandTotal, 0)} ฿
                            </span>
                            <span>{isOpen ? '▲ ย่อ' : '▼ ขยาย'}</span>
                        </div>
                        {isOpen && (
                            !summary ? <p style={{ padding: '12px 16px', color: '#6B6B6B' }}>โหลดข้อมูลไม่สำเร็จ</p> : (
                            <div style={{ padding: '12px 16px' }}>
                                <h4 style={{ margin: '4px 0' }}>รายการสินค้า</h4>
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginBottom: '12px' }}>
                                        <thead>
                                            <tr>
                                                <th style={{ ...headerStyle, textAlign: 'left' }}>รายการ</th>
                                                <th style={{ ...headerStyle, textAlign: 'right' }}>ขาย (จำนวน)</th>
                                                <th style={{ ...headerStyle, textAlign: 'right' }}>ขาย (บาท)</th>
                                                <th style={{ ...headerStyle, textAlign: 'right' }}>เติม</th>
                                                <th style={{ ...headerStyle, textAlign: 'right' }}>เหลือ</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {summary.products.length === 0 && (
                                                <tr><td style={rowStyle} colSpan={5}>ไม่มีข้อมูล</td></tr>
                                            )}
                                            {summary.products.map(p => (
                                                <tr key={p.name}>
                                                    <td style={rowStyle}>{p.name}</td>
                                                    <td style={{ ...rowStyle, textAlign: 'right' }}>{fmtInt(p.sellQty)}</td>
                                                    <td style={{ ...rowStyle, textAlign: 'right' }}>{fmt(p.sellTotal, 2)}</td>
                                                    <td style={{ ...rowStyle, textAlign: 'right' }}>{fmtInt(p.refillQty)}</td>
                                                    <td style={{ ...rowStyle, textAlign: 'right' }}>{fmtInt(p.cargoQty)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                <h4 style={{ margin: '4px 0' }}>กระสอบ</h4>
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginBottom: '12px' }}>
                                        <thead>
                                            <tr>
                                                <th style={{ ...headerStyle, textAlign: 'left' }}>รายการ</th>
                                                <th style={{ ...headerStyle, textAlign: 'right' }}>กระสอบกลับมา</th>
                                                <th style={{ ...headerStyle, textAlign: 'right' }}>กระสอบขาดเกิน</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {summary.bagReturns.length === 0 && (
                                                <tr><td style={rowStyle} colSpan={3}>ไม่มีข้อมูล</td></tr>
                                            )}
                                            {summary.bagReturns.map(b => (
                                                <tr key={b.name}>
                                                    <td style={rowStyle}>{b.name}</td>
                                                    <td style={{ ...rowStyle, textAlign: 'right' }}>{fmtInt(b.back)}</td>
                                                    <td style={{ ...rowStyle, textAlign: 'right', color: b.deficit > 0 ? '#E60012' : 'inherit' }}>{fmtInt(b.deficit)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '12px', fontSize: '13px' }}>
                                    <span>ยอดขายรวม: <b>{fmt(summary.saleTotal, 2)} ฿</b></span>
                                    <span>โปรโมชั่น 3ถุง100: {fmtInt(summary.promoUsageCount)} × 5 = <b>{fmtInt(summary.promoUsageCount * 5)} ฿</b></span>
                                    <span>ค่าน้ำมัน: <b>{fmt(summary.fuelTotal, 2)} ฿</b></span>
                                    <span>ยอดโอน: <b>{fmt(summary.transferTotal, 2)} ฿</b></span>
                                </div>

                                <h4 style={{ margin: '4px 0' }}>แจกแจงเงินสด</h4>
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                        <thead>
                                            <tr>
                                                <th style={{ ...headerStyle, textAlign: 'left' }}>ประเภท</th>
                                                <th style={{ ...headerStyle, textAlign: 'right' }}>จำนวน</th>
                                                <th style={{ ...headerStyle, textAlign: 'right' }}>ยอดเงิน</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {summary.denomList.map(d => (
                                                <tr key={d.value}>
                                                    <td style={rowStyle}>{d.name}</td>
                                                    <td style={{ ...rowStyle, textAlign: 'right' }}>{fmtInt(summary.cashDenoms[d.value]?.count || 0)}</td>
                                                    <td style={{ ...rowStyle, textAlign: 'right' }}>{fmt(summary.cashDenoms[d.value]?.total || 0, 2)}</td>
                                                </tr>
                                            ))}
                                            <tr>
                                                <td style={{ ...rowStyle, fontWeight: 700 }}>รวม</td>
                                                <td style={rowStyle}></td>
                                                <td style={{ ...rowStyle, textAlign: 'right', fontWeight: 700 }}>{fmt(summary.cashGrandTotal, 2)}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            )
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default DriverDailyReport;
